import { Resource } from '@foundation/utils';

/** `draft` is editable and deletable; `issued` is neither, ever. There is no third state. */
export type DocumentStatus = 'draft' | 'issued';

/** Who is issuing, or who is billed. Frozen onto the document — a snapshot, not a lookup, so a
 * company that moves next year does not rewrite the invoices it sent this year. */
export interface DocumentParty {
	name: string;
	addressLine1?: string | null;
	addressLine2?: string | null;
	postalCode?: string | null;
	city?: string | null;
	/** ISO 3166-1 alpha-2. Decides the VAT treatment, and required by e-reporting. */
	country?: string | null;
	email?: string | null;
	vatNumber?: string | null;
	siren?: string | null;
	/** The establishment behind the SIREN. What the annuaire a plateforme agréée resolves routes
	 * on, and what BR-FR-09 checks agrees with the SIREN. */
	siret?: string | null;
	legalForm?: string | null;
	shareCapital?: string | null;
	rcs?: string | null;
}

export interface DocumentLine {
	label: string;
	quantity: number;
	/** Excluding VAT, because that is what EN 16931 asks for and what a breakdown is built from.
	 * Never negative: a reduction is a discount on a positive line (BR-27). */
	unitPriceHt: number;
	vatRate: number;
}

export interface VatBreakdownEntry {
	rate: number;
	baseHt: number;
	vatAmount: number;
	/** The article invoked when the rate is zero, e.g. the franchise en base. */
	exemptionMention?: string | null;
}

export interface DocumentTotals {
	totalHt: number;
	totalVat: number;
	totalTtc: number;
	/** One entry per distinct rate — EN 16931 makes this repeatable for exactly that reason. */
	vatBreakdown: VatBreakdownEntry[];
}

/** A second currency shown for information only. The rate is frozen here: a document whose figures
 * change next year is not a document. */
export interface IndicativeConversion {
	currency: string;
	rate: number;
	rateDate: string;
	source?: string | null;
	totalTtc: number;
}

export interface DocumentConfig {
	seller?: DocumentParty | null;
	buyer?: DocumentParty | null;
	currency: string;
	lines: DocumentLine[];
	totals: DocumentTotals;
	/** The second currency to quote, if any. The choice; `indicativeConversion` is what the server
	 * derives from it, and is never sent -- the same split as lines and totals. */
	indicativeCurrency?: string | null;
	indicativeConversion?: IndicativeConversion | null;
	/** Provenance only. Which ledger lines prefilled the draft — never a link the document depends
	 * on, because an invoice is an independent legal object. */
	billingEventIds: string[];
	/** Why a zero-rated line is exempt (BT-120), printed and carried in the XML. EN 16931 refuses a
	 * document whose VAT category is "exempt" without one: a rate of zero is a claim that needs its
	 * article. */
	vatExemption?: string | null;
	/** The rendered document, stored once and never re-rendered. An EntityFile id. */
	pdfFileId?: string | null;
	notes?: string | null;
}

/** The mentions art. L441-9 requires. Not optional: the backend model requires them and nothing
 * fills them in, so an invoice that does not carry them is refused rather than completed. */
export interface InvoiceConfig extends DocumentConfig {
	/** ISO date (YYYY-MM-DD). */
	issueDate: string;
	dueDate: string;
	paymentTerms: string;
	references?: string | null;
}

export interface Invoice extends Resource {
	name?: string | null;
	description?: string | null;
	/** The admin's own note on the document. Not printed, not the customer's business. */
	adminDetails?: string | null;
	/** `draft-SP-FA-2026-00042` while a draft: a prediction, allocated to nobody. The prefix drops
	 * the moment the number is really taken. */
	number: string;
	status: DocumentStatus;
	beneficiaryId?: string | null;
	timeIssued?: string | null;
	config: InvoiceConfig;
}

/** Set on a preview that printed without its XML, naming what the draft still lacks. The body of
 * that response is the PDF, so a header is the only place the reason can travel. */
export const FACTURX_MISSING_HEADER = 'X-Facturx-Missing';

/** One thing an outside validator objected to. `stage` is the interesting field: it says whether
 * the objection is to the PDF/A container or to the XML inside it. */
export interface ValidationIssue {
	message: string;
	code?: string | null;
	severity?: string | null;
	stage?: string | null;
	xpath?: string | null;
}

/** Where what the invoice says about a party differs from the public registry. Not a rule and not
 * a reason the file is invalid: it is the check no ruleset can make, because it needs to look the
 * company up. A SIRET belonging to somebody else survives every rule we run ourselves. */
export interface RegistryCheck {
	message: string;
	ruleId?: string | null;
	severity?: string | null;
	party?: string | null;
	expected?: string | null;
	declared?: string | null;
}

/** What an engine that is not ours made of a document. `targetExecuted` matters as much as
 * `valid`: a pass on a target that never ran is not a pass. */
export interface ValidationReport {
	valid: boolean;
	profile?: string | null;
	targetRequested?: string | null;
	targetExecuted?: string | null;
	targetStatus?: string | null;
	scope?: string | null;
	profilesApplied: string[];
	profilesSkipped: string[];
	errorCount: number;
	warningCount: number;
	issues: ValidationIssue[];
	registryChecks: RegistryCheck[];
}

/** One exemption an admin can pick for a zero-rated document. `code` is BT-121, `mention` is
 * BT-120 — the sentence that gets printed and sent. */
export interface VatExemption {
	code: string;
	label: string;
	mention: string;
}

/** What `targetExecuted` says when nothing left the building: the check ran here, against the same
 * rules, and the panel must not present it as a third party's verdict. */
export const IN_PROCESS_TARGET = 'in_process';
